import {
  css,
  margin,
  mq,
  spacing,
  styled,
  Text,
} from '@thg-commerce/enterprise-theme'
import { Button, SafeHtml } from '@thg-commerce/gravity-elements'
import { Grid, GridItem, Picture } from '@thg-commerce/gravity-system'
import { dangerous_mqMax } from '@thg-commerce/gravity-theme'
import { Margin } from '@thg-commerce/gravity-theme/margin'

import { TextHorizontalAlignment } from './Banner'

export const BANNER_MAX_SIZE = {
  xs: {
    width: '600px',
    height: 'auto',
  },
  sm: {
    width: '1200px',
  },
  lg: {
    width: '1920px',
  },
}

export const BannerContainer = styled(Grid)<{
  containBanner?: boolean
  marginBottom?: string
  spacingAdjust?: boolean
  textAlign?: TextHorizontalAlignment
  hideGap?: boolean
  hasMargin?: string
  backgroundColor?: string
  primaryBannerStyle?: boolean
}>`
  width: 100%;
  max-width: ${(props) =>
    props.containBanner
      ? props.theme.site.siteWidth
      : BANNER_MAX_SIZE.lg.width};
  margin: ${(props) =>
    props.hasMargin === 'both'
      ? `${spacing(4)} auto`
      : props.hasMargin === 'top'
      ? `${spacing(4)} auto 0`
      : props.hasMargin === 'bottom'
      ? `0 auto ${spacing(4)}`
      : props.hasMargin === 'none'
      ? `0`
      : `0 auto ${props.marginBottom || spacing(4)}`};

  grid-template-rows: auto 2fr;
  height: ${(props) => props.spacingAdjust && BANNER_MAX_SIZE.xs.height};
  text-align: ${(props) =>
    props.primaryBannerStyle ? 'left' : props.textAlign || 'center'};
  gap: ${(props) =>
    props.hideGap
      ? 0
      : `calc(${props.theme.grid.columnGap} / 2)
      calc(${props.theme.grid.rowGap} * 2);`};
  background-color: ${(props) => props.backgroundColor || 'transparent'};
`

export const ImageItem = styled(GridItem)`
  margin: 0;
  min-width: 0;
`

export const StyledPicture = styled(Picture)<{
  spacingAdjust?: boolean
}>`
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: auto 4 / 3;
  max-width: ${BANNER_MAX_SIZE.xs.width};

  ${(props) => props.spacingAdjust && 'object-fit: cover; height: 100%;'}

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    max-width: ${BANNER_MAX_SIZE.sm.width};
    aspect-ratio: auto 5 / 2;
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
    max-width: ${BANNER_MAX_SIZE.lg.width};
    aspect-ratio: auto 7 / 2;
  }
`

export const TextContainer = styled(GridItem)<{
  increaseMaxWidth?: boolean
  removePointerEvents?: boolean
  spacingAdjust?: boolean
  hasMoreButtons?: boolean
  primaryBannerStyle?: boolean
}>`
  display: flex;

  align-items: ${(props) =>
    props.primaryBannerStyle
      ? 'end'
      : !props.hasMoreButtons
      ? props.theme.widget.banner.textAlignment
      : 'flex-end'};
  width: 100%;
  max-width: ${(props) =>
    props.increaseMaxWidth || props.spacingAdjust
      ? BANNER_MAX_SIZE.lg.width
      : props.theme.site.siteWidth};
  padding: 0 ${spacing(2)};
  min-width: 0;

  ${(props) => props.removePointerEvents && 'pointer-events: none;'}

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    margin: ${spacing(4)} auto;
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    margin: ${(props) => (props.spacingAdjust ? spacing(3) : spacing(2))} 0
      ${spacing(2)};
    ${(props) => props.spacingAdjust && `padding: 0 ${spacing(6)}`};
    align-items: ${(props) =>
      !props.hasMoreButtons
        ? props.theme.widget.banner.textAlignment
        : 'flex-end'};
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
    padding: 0
      ${(props) =>
        props.increaseMaxWidth
          ? spacing(props.theme.widget.banner.spacePadding)
          : spacing(2)};
    margin: ${spacing(5)} auto;

    align-items: ${(props) =>
      props.primaryBannerStyle
        ? 'end'
        : !props.hasMoreButtons
        ? props.theme.widget.banner.textAlignment
        : 'flex-end'};
  }
`

export const TextGrid = styled(Grid)<{
  spacingAdjust?: boolean
  primaryBannerStyle?: boolean
}>`
  width: 100%;
  ${(props) => margin(props.theme.widget.banner.textContainer.margin)};
  ${(props) =>
    props.spacingAdjust && `grid-template-columns: repeat( 18, [col] 1fr );`}
  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    ${margin({ top: 0, right: 0, bottom: 0, left: 0 })}
    ${(props) =>
      props.spacingAdjust && `grid-template-columns: repeat( 24, [col] 1fr );`}
  }
  && {
    grid-gap: 0;
  }
`

export const TextGridItem = styled(GridItem)<{
  containerBackground?: string
  hasMoreButtons?: boolean
  primaryBannerStyle?: boolean
}>`
  margin: 0;
  width: ${(props) => props.hasMoreButtons && '100%'};

  background: ${(props) =>
    props.containerBackground === 'white' &&
    props.theme.colors.palette.greys.white};

  ${(props) => mq(props.theme.breakpointUtils.map, 'xs')} {
    justify-self: ${(props) =>
      props.primaryBannerStyle ? 'flex-start' : 'center'};
    margin-bottom: ${(props) => (props.primaryBannerStyle ? '1.5rem' : 0)};
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    padding: ${(props) => props.containerBackground === 'white' && spacing(3)};
    justify-self: ${(props) =>
      props.justifySelf ? props.justifySelf : 'auto'};
    margin-bottom: ${(props) => (props.primaryBannerStyle ? '1.5rem' : 0)};
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    padding: ${(props) => props.containerBackground === 'white' && spacing(4)};
  }

  justify-self: ${(props) =>
    props.primaryBannerStyle ? 'flex-start' : 'start'};
`

type TextProps = {
  lightText?: boolean
  spacingAdjust?: boolean
  containerBackground?: string
  textAlign?: TextHorizontalAlignment
  centerText?: boolean
  enableBannerTextOverlay?: boolean
  hasMoreButtons?: boolean
  subtitleTextColor?: string
  headingTextColor?: string
  primaryBannerStyle?: boolean
}

export const Title = styled(SafeHtml)<TextProps>`
  color: ${(props) =>
    props.headingTextColor
      ? props.headingTextColor
      : props.lightText
      ? props.theme.colors.palette.greys.white
      : props.theme.colors.palette.greys.darker};

  ${(props) =>
    props.spacingAdjust
      ? Text(
          props.theme.widget.GlobalPrimaryBanner.title.textStyle.entry,
          props.theme.widget.GlobalPrimaryBanner.title.textStyle.style,
          props.theme.widget.GlobalPrimaryBanner.title.textStyle.hasScaledText,
        )
      : Text(
          props.theme.widget.GlobalPrimaryBanner.title.text.textStyle.entry,
          props.theme.widget.GlobalPrimaryBanner.title.text.textStyle.style,
          props.theme.widget.GlobalPrimaryBanner.title.textStyle.hasScaledText,
        )};
  width: 100%;
  margin-bottom: ${spacing(2)};
  padding: ${(props) =>
    !props.primaryBannerStyle && props.textAlign === 'center'
      ? `0 ${spacing(3)} 0`
      : 0};
  text-align: ${(props) =>
    props.primaryBannerStyle ? 'left' : props.textAlign || 'center'};

  ${(props) =>
    props.lightText &&
    props.enableBannerTextOverlay &&
    `color: ${
      props.headingTextColor
        ? props.headingTextColor
        : props.theme.colors.palette.greys.white
    };`}

  ${(props) => mq(props.theme.breakpointUtils.map, 'xs')} {
    text-align: ${(props) =>
      props.primaryBannerStyle
        ? 'left'
        : props.theme.widget.GlobalPrimaryBanner.textContainer
            .mobileAlignment || props.textAlign};
    ${(props) =>
      props.primaryBannerStyle &&
      css`
        ${Text(
          props.theme.widget.banner?.title?.primaryTitle?.textStyle?.sm?.entry,
          props.theme.widget.banner?.title?.primaryTitle?.textStyle?.sm?.style,
        )}

        ${props.headingTextColor && `color: ${props.headingTextColor};`}
      `}
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    text-align: ${(props) =>
      props.primaryBannerStyle ? 'left' : props.textAlign || 'center'};
    ${(props) =>
      props.spacingAdjust
        ? Text(
            props.theme.widget.GlobalPrimaryBanner.title.textStyle.entry,
            props.theme.widget.GlobalPrimaryBanner.title.textStyle.style,
            props.theme.widget.GlobalPrimaryBanner.title.textStyle
              .hasScaledText,
          )
        : Text(
            props.theme.widget.GlobalPrimaryBanner?.title?.titleSmallfont
              ?.textStyle.entry ||
              props.theme.widget.GlobalPrimaryBanner.title.text.textStyle.entry,
            props.theme.widget.GlobalPrimaryBanner?.title?.titleSmallfont
              ?.textStyle.style ||
              props.theme.widget.GlobalPrimaryBanner.title.text.textStyle.style,
          )};
    ${(props) => props.centerText && 'text-align: center;'}
    ${(props) =>
      props.lightText &&
      `color: ${
        props.headingTextColor
          ? props.headingTextColor
          : props.theme.colors.palette.greys.white
      };`}
    padding: ${(props) => (!props.primaryBannerStyle ? `0 ${spacing(3)}` : 0)};
    width: ${(props) =>
      props.theme.widget.GlobalPrimaryBanner.title.smallDevicesWidth ||
      props.theme.widget.GlobalPrimaryBanner.title.width};

    ${(props) =>
      props.primaryBannerStyle &&
      css`
        ${Text(
          props.theme.widget.banner?.title?.primaryTitle?.textStyle?.sm?.entry,
          props.theme.widget.banner?.title?.primaryTitle?.textStyle?.sm?.style,
        )}

        ${props.headingTextColor && `color: ${props.headingTextColor};`}
      `}
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    width: 100%;
    text-align: ${(props) =>
      props.primaryBannerStyle ? 'left' : props.textAlign || 'center'};
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
    width: ${(props) => props.theme.widget.GlobalPrimaryBanner.title.width};
    white-space: ${(props) =>
      props.theme.widget.GlobalPrimaryBanner.title.whiteSpace
        ? props.theme.widget.GlobalPrimaryBanner.title.whiteSpace
        : props.primaryBannerStyle
        ? 'nowrap'
        : 'normal'};

    text-align: ${(props) =>
      props.primaryBannerStyle ? 'left' : props.textAlign || 'center'};
    padding: ${(props) => (!props.primaryBannerStyle ? `0 ${spacing(3)}` : 0)};
    ${(props) =>
      props.spacingAdjust
        ? Text(
            props.theme.widget.GlobalPrimaryBanner.title.textStyle.entry,
            props.theme.widget.GlobalPrimaryBanner.title.textStyle.style,
            props.theme.widget.GlobalPrimaryBanner.title.textStyle
              .hasScaledText,
          )
        : Text(
            props.theme.widget.GlobalPrimaryBanner?.title?.titleLargefont
              ?.textStyle.entry ||
              props.theme.widget.GlobalPrimaryBanner.title.text.textStyle.entry,
            props.theme.widget.GlobalPrimaryBanner?.title?.titleLargefont
              ?.textStyle.style ||
              props.theme.widget.GlobalPrimaryBanner.title.text.textStyle.style,
          )};
    color: ${(props) =>
      props.headingTextColor
        ? props.headingTextColor
        : props.lightText
        ? props.theme.colors.palette.greys.white
        : props.theme.colors.palette.greys.darker};
    text-transform: ${(props) =>
      props.theme.widget.GlobalPrimaryBanner.title?.textTransform || 'none'};
  }
  text-transform: ${(props) =>
    props.theme.widget.GlobalPrimaryBanner.title?.textTransform || 'none'};

  ${(props) =>
    props.hasMoreButtons &&
    css`
      & {
        ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
          font-size: ${props.theme.widget.GlobalPrimaryBanner.title
            ?.titleExtraLarge?.textStyle?.lg?.fontSize || ''};
          line-height: ${props.theme.widget.GlobalPrimaryBanner.title
            ?.titleExtraLarge?.textStyle?.lg?.lineHeight || ''};
        }
        font-size: ${props.theme.widget.GlobalPrimaryBanner.title
          ?.titleExtraLarge?.textStyle?.sm?.fontSize || ''};
        line-height: ${props.theme.widget.GlobalPrimaryBanner.title
          ?.titleExtraLarge?.textStyle?.sm?.lineHeight || ''};
      }
    `}
`
export const Subtitle = styled(SafeHtml)<TextProps>`
  ${(props) =>
    css`
      ${Text(
        props.theme.widget.banner.subtitle.textStyle.entry,
        props.theme.widget.banner.subtitle.textStyle.style,
      )};
    `}
  ${(props) => props.hasMoreButtons && Text('xlarge2', 'alternate')};
  color: ${(props) =>
    props.subtitleTextColor
      ? props.subtitleTextColor
      : props.containerBackground === 'white'
      ? props.theme.colors.palette.greys.dark
      : props.theme.colors.palette.greys.darker};

  ${(props) =>
    props.lightText &&
    props.enableBannerTextOverlay &&
    `color: ${
      props.subtitleTextColor
        ? props.subtitleTextColor
        : props.theme.colors.palette.greys.white
    };`}

  &:not(:last-child) {
    margin-bottom: ${(props) =>
      props.hasMoreButtons ? spacing(0.5) : spacing(4)};
  }
  text-align: ${(props) => props.textAlign || 'center'};
  width: ${(props) => props.theme.widget.GlobalPrimaryBanner.subtitle.width};
  padding: ${(props) =>
    !props.primaryBannerStyle && props.textAlign === 'center'
      ? `0 ${spacing(3)} 0`
      : 0};
  ${(props) => mq(props.theme.breakpointUtils.map, 'xs')} {
      text-align: ${(props) =>
        props.primaryBannerStyle
          ? 'left'
          : props.theme.widget.GlobalPrimaryBanner.textContainer
              .mobileAlignment || props.textAlign};
  }
     ${(props) =>
       props.primaryBannerStyle &&
       css`
         ${Text(
           props.theme.widget.banner?.subtitle?.textStyle?.primary?.sm
             ?.textStyle?.entry,
           props.theme.widget.banner?.subtitle?.textStyle?.primary?.sm
             ?.textStyle?.style,
         )}
         ${props.subtitleTextColor && `color: ${props.subtitleTextColor};`}
       `}
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    text-align: ${(props) =>
      props.primaryBannerStyle ? 'left' : props.textAlign || 'center'};
    width: ${(props) =>
      props.theme.widget.GlobalPrimaryBanner.title.smallDevicesWidth};
    ${(props) => props.centerText && 'text-align: center'};
    ${(props) =>
      props.lightText &&
      `color: ${
        props.subtitleTextColor
          ? props.subtitleTextColor
          : props.theme.colors.palette.greys.white
      };`}
      padding: ${(props) =>
        !props.primaryBannerStyle ? `0 ${spacing(3)}` : 0};

     ${(props) =>
       props.primaryBannerStyle &&
       css`
         ${Text(
           props.theme.widget.banner?.subtitle?.textStyle?.primary?.sm
             ?.textStyle?.entry,
           props.theme.widget.banner?.subtitle?.textStyle?.primary?.sm
             ?.textStyle?.style,
         )}
         ${props.subtitleTextColor && `color: ${props.subtitleTextColor};`}
       `}
  }

   ${(props) =>
     props.primaryBannerStyle &&
     css`
       ${mq(props.theme.breakpointUtils.map, 'lg')} {
         white-space: nowrap;

         ${Text(
           props.theme.widget.banner?.subtitle?.textStyle?.primary?.lg
             ?.textStyle?.entry,
           props.theme.widget.banner?.subtitle?.textStyle?.primary?.lg
             ?.textStyle?.style,
         )}
         ${props.subtitleTextColor && `color: ${props.subtitleTextColor};`}
       }
     `}
`

export const ButtonsContainer = styled.div<{
  textAlign?: TextHorizontalAlignment
  centerText?: boolean
  hasMoreButtons?: boolean
  primaryBannerStyle?: boolean
}>`
  display: flex;
  flex-wrap: wrap;
  width: ${(props) => (props.hasMoreButtons ? '100%' : 'fit-content')};
  flex-direction: column;
  justify-content: ${(props) => props.textAlign || 'center'};
  ${(props) => props.textAlign === 'center' && 'margin: auto;'}
  margin: ${(props) =>
    props.primaryBannerStyle
      ? '0'
      : props.textAlign === 'center'
      ? 'auto'
      : '0'};

  ${(props) =>
    props.hasMoreButtons &&
    css`
      ${dangerous_mqMax(props.theme.breakpointUtils.map, 'lg')} {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        row-gap: ${spacing(2)};
        column-gap: ${spacing(1.25)};
        width: 100%;
        max-width: 100%;
        margin: ${spacing(4)} 0;
      }

      ${dangerous_mqMax(props.theme.breakpointUtils.map, 'sm')} {
        grid-template-columns: repeat(2, 1fr);
      }

      ${mq(props.theme.breakpointUtils.map, 'lg')} {
        flex-wrap: noWrap;
        margin: ${spacing(5.5)} 0;
      }
    `}

  ${(props) => mq(props.theme.breakpointUtils.map, 'xs')} {
    flex-direction: ${(props) =>
      props.theme.widget.GlobalPrimaryBanner.textContainer.mobileDirection};

    justify-self: ${(props) =>
      props.theme.widget.GlobalPrimaryBanner.textContainer.mobileAlignment ||
      'auto'};
    justify-content: ${(props) =>
      props.primaryBannerStyle ? 'flex-start' : 'center'};
    display: ${(props) =>
      props.theme.widget.GlobalPrimaryBanner.textContainer.display};
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    justify-self: ${(props) =>
      props.primaryBannerStyle ? 'flex-start' : props.textAlign || 'center'};
    justify-content: ${(props) =>
      props.primaryBannerStyle ? 'flex-start' : 'center'};
    flex-wrap: nowrap;
    flex-direction: row;
    padding: ${(props) => (!props.primaryBannerStyle ? `0 ${spacing(3)}` : 0)};
    display: flex;
  }

  ${(props) =>
    !props.primaryBannerStyle &&
    css`
      ${mq(props.theme.breakpointUtils.map, 'lg')} {
        justify-content: center;
      }
    `}
`

export const StyledButton = styled(Button)<{ hasMoreButtons?: boolean }>`
  height: fit-content;

  ${(props) => !props.hasMoreButtons && `margin-bottom: ${spacing(2)};`}

  pointer-events: all;
  ${(props) =>
    props.theme.widget.banner?.button?.borderWidth &&
    `
    &, &:hover {
      border-width: ${props.theme.widget.banner?.button?.borderWidth};
    }
  `}

  ${(props) => mq(props.theme.breakpointUtils.map, 'xs')} {
    ${(props) =>
      props.theme.widget.GlobalPrimaryBanner.textContainer.mobileAlignment &&
      `
      &:not(:last-child) {
        margin-right: ${spacing(0.5)};
      }
      &:last-child {
        margin-left: ${spacing(0.5)};
      }`}
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    height: auto;
    margin-bottom: 0;
    &:not(:last-child) {
      margin-right: ${(props) =>
        props.hasMoreButtons ? spacing(1.25) : spacing(2)};
    }
  }

  ${(props) =>
    props.theme.widget.banner?.linkHover &&
    `
    &:hover > a, &:active > a {
      text-decoration: ${
        props.theme.widget.banner.linkHover.textDecoration || 'inherit'
      }
    }
  `}

  ${(props) =>
    props.hasMoreButtons &&
    css`
      ${dangerous_mqMax(props.theme.breakpointUtils.map, 'lg')} {
        a,
        a:hover,
        a:active,
        a:focus {
          padding: ${spacing(1.25)};
        }
        &:not(:last-child) {
          margin-right: 0;
        }
        &:last-child {
          margin-left: 0;
        }
      }
    `}
`

export const StyledLink = styled.a`
  display: block;
  &:focus-visible {
    outline: ${(props) => props.theme.colors.palette.brand.base} solid 3px;
    outline-offset: -3px;
  }
`

export const HighlightContainer = styled.p<TextProps>`
  ${Text('bodyText', 'alternate')};
  margin-bottom: ${spacing(2)};
  color: ${(props) =>
    props.containerBackground === 'white'
      ? props.theme.colors.palette.greys.dark
      : props.theme.colors.palette.greys.darker};

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    ${(props) => props.centerText && 'text-align: center'};
    ${(props) =>
      props.lightText && `color: ${props.theme.colors.palette.greys.dark};`}
  }
`

export const StyledLogoImage = styled(Picture)<{
  margin?: Margin
}>`
  max-width: 100%;
  ${(props) => mq(props.theme.breakpointUtils.map, 'xs')} {
    padding: 0 ${spacing(4)} 0;
    margin: ${spacing(2)} auto;
  }
  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    padding: 0 ${spacing(8)} 0;
    ${(props) => props.margin && margin(props.margin)};
  }
  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    padding: 0 ${spacing(2)} 0;
  }
  ${(props) => props.margin && margin(props.margin)};
`
